import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, ArrowPathIcon, CheckIcon, LockClosedIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { rateLine } from '../../utils/rateBasis';
import { track, EVENTS } from '../../services/analytics';
import { Crest } from '../UniversityCrest';
import { entryProps, storeCarriedSchools } from '../../utils/schoolEntry';
import { schoolChipLabel, tryHeroSchoolLine } from '../../content/schoolEntry';
import { ReasonLine } from './astraLite/ReadCard';
import { driverTones, majorNoteTone } from '../../utils/readTone';
import { BAND_CHIP_UNKNOWN } from '../../utils/fitBands';
import { gpaField } from '../../utils/gpaScale';

// "Try it before you sign in" landing section.
//
// Shape: paragraph -> profile chips -> "Where do you stand at ___?" -> the
// visitor picks THEIR school and gets its deterministic read (band + the
// percentile-vs-their-admitted-class drivers - the insider-data demo moment).
// We never volunteer a school list unasked: an unsolicited pick that clashes
// with the visitor's mental model reads as "the engine is wrong".
//
// Locked cards under the newest read make the paid depth visible (outcomes,
// gap plan, major chances) - the free read is the surface, and it says so.
//
// GPA gates all reads (a read with no GPA can only deter); the one clarifying
// ask is answered inline and recomputed deterministically - no second LLM
// call. Nothing is persisted server-side; the draft rides localStorage into
// signup.

const TRY_URL = import.meta.env.VITE_TRY_HERO_URL
    || `${import.meta.env.VITE_PROFILE_MANAGER_V2_URL || 'https://profile-manager-v2-pfnwjfp26a-ue.a.run.app'}/try-instant-read`;

// Shipped ON (approved 2026-07-17). VITE_DISABLE_TRY_HERO=1 is the kill switch.
export const TRY_HERO_ENABLED = import.meta.env.VITE_DISABLE_TRY_HERO !== '1';

const PLACEHOLDER = "I'm a junior at Lincoln High in San Jose. 3.8 GPA, 1450 SAT. President of the robotics club, volunteer tutor on weekends. Thinking about mechanical engineering.";

// One-click personas that fill the box with an EDITABLE paragraph - a visitor
// who won't type from scratch will still tweak an example into their own kid.
export const SEEDS = [
    {
        key: 'detailed',
        label: 'A detailed example',
        text: "I'm a senior at Westview High School in Portland, Oregon. Unweighted GPA 3.92, weighted 4.4. SAT 1520 (760 math, 760 reading) and a 34 on the ACT. I've taken 9 AP classes - Biology, Chemistry, Calculus BC, Physics C, US History, English Language, Psychology, Environmental Science, and Statistics - mostly 5s with a couple of 4s. I'm captain of the varsity swim team (about 12 hours a week for four years), and I founded and lead our school's Marine Conservation Club - 30 members, we run monthly beach cleanups. I volunteer at the Oregon Coast Aquarium roughly 5 hours a week, won the state science fair in 10th grade for a project on microplastics, and I work a summer job at a local dive shop. I want to study Marine Biology, and ocean conservation is what I care about most. I'm a first-generation college student and I'll need financial aid.",
    },
    {
        key: 'engineering',
        label: 'Engineering junior',
        text: "I'm a junior at Lincoln High in San Jose, California. 3.8 unweighted GPA, 1450 SAT. I'm president of the robotics club and volunteer as a math tutor on weekends. Took 6 APs so far, mostly 4s and 5s. Thinking about mechanical engineering.",
    },
    {
        key: 'business',
        label: 'Business senior, no test yet',
        text: "Senior at Dougherty Valley High in San Ramon, California with a 3.95 unweighted GPA. 8 AP classes done, 5 more this year, all 5s on my exams. I run our DECA chapter and started a small resale business with my brother. Haven't taken the SAT yet. I want to study business.",
    },
    {
        key: 'undecided',
        label: 'Sophomore, undecided',
        text: "I'm a sophomore with a 3.6 GPA. I play varsity soccer and I'm on the debate team. My parents want me to start thinking about college but honestly I have no idea what I want to study yet.",
    },
];

const BAND_CHIP = {
    Safety: 'bg-[#E8F0E9] text-[#1A4D2E] border-[#CFE0D2]',
    Target: 'bg-[#1A4D2E] text-white border-[#1A4D2E]',
    Reach: 'bg-[#FCEEE8] text-[#C05838] border-[#F0D5C8]',
};

const FIELD_LABELS = [
    ['name', 'Name'], ['school', 'School'], ['location', 'Location'], ['grade', 'Grade'],
    ['gpa_unweighted', 'Unweighted GPA'], ['gpa_weighted', 'Weighted GPA'], ['sat_total', 'SAT'],
    ['act_composite', 'ACT'], ['ap_courses_count', 'AP/IB courses'], ['intended_major', 'Intended major'],
];

const LOCKED_CARDS = [
    { title: 'Real reported outcomes here', text: 'What actually happened to applicants with records like yours.' },
    { title: 'What closes your gap', text: 'The specific moves - courses, tests, story - that change this read.' },
    { title: 'Chances by major + essay angles', text: 'The full per-program analysis, not the surface estimate.' },
];

const inputCls = 'rounded-lg border border-[#E0DED8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30';

// Cycles through what the reader is actually doing. Honest stages, honest
// clock - never a fake percent bar.
const READING_STAGES = [
    'Reading your record\u2026',
    'Pulling the real admitted-class numbers\u2026',
    'Checking your numbers against them\u2026',
    'Almost there \u2014 about 10 seconds in all\u2026',
];
function ReadingTicker() {
    const [i, setI] = React.useState(0);
    React.useEffect(() => {
        const t = setInterval(() => setI((n) => Math.min(n + 1, READING_STAGES.length - 1)), 3200);
        return () => clearInterval(t);
    }, []);
    return <span>{READING_STAGES[i]}</span>;
}

async function postJson(body) {
    const res = await fetch(TRY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    return res.json();
}

// `heading` and `sub`: a host page may set the box's heading and the line
// under it (the landing page's come from content/landingHome.js, founder pick
// 2026-09-29). Everything inside the box stays word for word; the V1 page
// keeps the defaults.
const DEFAULT_HEADING = 'Where do you stand? Just type it.';
const DEFAULT_SUB = "Tell us about yourself like you'd text a friend, then ask about any school. We'll read your record against their real admitted class. Free, before you even sign in.";

// `school`: the entry a college page sent (utils/schoolEntry.js, #2608), or
// null. With one, the line under the heading names the school, the box shows
// it pre-set, and the first read after the picture is that school's, with no
// typing. Without one, nothing here changes, props on the events included.
export default function TryHero({ onSignIn, heading = DEFAULT_HEADING, sub = DEFAULT_SUB, school = null }) {
    const [text, setText] = useState('');
    const [submittedText, setSubmittedText] = useState('');
    const [phase, setPhase] = useState('write');   // write | reading | result
    const [fields, setFields] = useState({});
    const [missing, setMissing] = useState([]);
    const [schools, setSchools] = useState([]);
    const [reads, setReads] = useState([]);        // newest first
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');
    const [gpaDraft, setGpaDraft] = useState('');

    const adopt = (data) => {
        setFields(data.fields || {});
        setMissing(data.missing || []);
        if (data.schools?.length) setSchools(data.schools);
    };

    // The schools the visitor checked ride along with the draft: after
    // sign-up the profile page offers to add them to the Launchpad (with the
    // credit cost stated) so the demo turns into the real list seamlessly.
    useEffect(() => {
        if (!reads.length) return;
        storeCarriedSchools(reads, school);
    }, [reads, school]);

    const ep = entryProps(school);

    const run = async (explicitText) => {
        const body = typeof explicitText === 'string' ? explicitText : text;
        if (body.trim().length < 40 || phase === 'reading') return;
        setPhase('reading');
        setError(null);
        track(EVENTS.TRY_HERO_SUBMITTED, { chars: body.length, ...ep });
        // Persist on the ATTEMPT, before the read: a rate-limited or failed
        // read says "sign in to keep going", so signing in must carry the
        // text even when no preview ever rendered (founder-hit bug, the
        // limit path dropped everything typed). Success paths refresh it.
        try { localStorage.setItem('scratchpad_draft_anonymous', body); } catch { /* private mode */ }
        if (school) storeCarriedSchools(reads, school);
        try {
            const data = await postJson({ text: body });
            if (data.success) {
                adopt(data);
                setSubmittedText(body);
                setPhase('result');
                track(EVENTS.TRY_HERO_PREVIEW_SHOWN, { fields: Object.keys(data.fields || {}).length, ...ep });
                // The school the visitor came for reads at once. A picture
                // with no GPA waits: the GPA answer runs it (refine, below).
                if (school && !(data.missing || []).includes('gpa')) {
                    await checkSchool({ id: school.id, name: school.name }, data.fields || {});
                }
            } else {
                setError(data.error || 'Something went sideways - try again in a moment.');
                setPhase('write');
            }
        } catch {
            setError('Could not reach the reader - try again in a moment.');
            setPhase('write');
        }
    };

    const checkSchool = async (opt, withFields = fields) => {
        if (busy) return null;
        setBusy(true);
        setError(null);
        track(EVENTS.TRY_HERO_SCHOOL_CHECKED, { school: opt.id, ...ep });
        try {
            const data = await postJson({ fields: withFields, school_id: opt.id });
            if (data.success && data.read) {
                setReads((prev) => [data.read, ...prev.filter((r) => r.university_id !== data.read.university_id)]);
                setQuery('');
            } else if (!data.success) {
                setError(data.error || 'Could not read that school - try again in a moment.');
            }
        } catch {
            setError('Could not reach the reader - try again in a moment.');
        }
        setBusy(false);
    };

    // The notes stay editable after the first read: a changed paragraph
    // re-extracts (a fresh Gemini read), then every school already on screen
    // re-scores against the NEW picture - deterministic re-reads, instant.
    const updateNotes = async () => {
        if (busy || text.trim().length < 40 || text.trim() === submittedText.trim()) return;
        setBusy(true);
        setError(null);
        track(EVENTS.TRY_HERO_SUBMITTED, { chars: text.length, revised: true, ...ep });
        // Same attempt-time persistence as run(): the latest edit survives
        // sign-in even if this re-read is rejected.
        try { localStorage.setItem('scratchpad_draft_anonymous', text); } catch { /* private mode */ }
        try {
            const data = await postJson({ text });
            if (data.success) {
                adopt(data);
                setSubmittedText(text);
                const rereads = await Promise.all(reads.map((r) =>
                    postJson({ fields: data.fields || {}, school_id: r.university_id }).catch(() => null)));
                setReads(rereads.filter((d) => d?.success && d.read).map((d) => d.read));
            } else {
                setError(data.error || 'Could not update - try again in a moment.');
            }
        } catch {
            setError('Could not reach the reader - try again in a moment.');
        }
        setBusy(false);
    };

    // The GPA answer folded in - deterministic recompute, no second LLM call.
    const refine = async (extra, fieldName) => {
        if (busy) return;
        setBusy(true);
        track(EVENTS.TRY_HERO_REFINED, { field: fieldName, ...ep });
        let readSchool = null;
        try {
            const data = await postJson({ fields: { ...fields, ...extra } });
            if (data.success) {
                adopt(data);
                if (school && !(data.missing || []).includes('gpa')
                    && !reads.some((r) => r.university_id === school.id)) {
                    readSchool = data.fields || { ...fields, ...extra };
                }
            }
        } catch { /* keep current read; ask stays visible */ }
        setBusy(false);
        if (readSchool) await checkSchool({ id: school.id, name: school.name }, readSchool);
    };

    const submitGpa = () => {
        const v = parseFloat(gpaDraft);
        if (Number.isNaN(v) || v <= 0 || v > 5) return;
        // The ask names no scale: the founder's rule files it (#5240).
        refine({ [gpaField(v)]: v }, 'gpa');
    };

    const keepIt = () => {
        if (school) track(EVENTS.TRY_HERO_SIGNIN_CLICKED, ep); else track(EVENTS.TRY_HERO_SIGNIN_CLICKED);
        try { localStorage.setItem('scratchpad_draft_anonymous', text); } catch { /* private mode */ }
        if (school) storeCarriedSchools(reads, school);
        onSignIn?.();
    };

    const chips = FIELD_LABELS
        .filter(([k]) => fields[k] !== undefined && fields[k] !== null && fields[k] !== '')
        .map(([k, label]) => ({ label, value: String(fields[k]) }));
    const activities = (fields.extracurriculars || []).slice(0, 5);
    const awards = (fields.awards || []).slice(0, 4);
    const jobs = (fields.work_experience || []).slice(0, 3);
    const firstGen = fields.first_generation === true;
    const gated = missing.includes('gpa');
    const matches = query.trim().length >= 2
        ? schools.filter((s) => s.name.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 8)
        : [];

    return (
        <div data-testid="try-hero" className="mx-auto max-w-3xl px-4 text-center">
            <h2 className="font-serif text-3xl font-semibold text-[#1A4D2E] [text-wrap:balance] sm:text-5xl">
                {heading}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-[#4A4A4A] [text-wrap:pretty]" data-testid={school ? 'try-hero-school-line' : undefined}>
                {school ? tryHeroSchoolLine(school.shortName, school.testBlind) : sub}
            </p>
            {school && phase !== 'result' && (
                <div data-testid="try-hero-preset-school" className="mx-auto mt-5 inline-flex max-w-full items-center gap-2 rounded-full border border-[#CFE0D2] bg-white py-1.5 pl-1.5 pr-4 text-sm text-[#1A4D2E] shadow-sm">
                    <Crest name={school.name} branding={school.branding} size={28} />
                    <span className="truncate"><span className="font-semibold">{schoolChipLabel}:</span> {school.name}</span>
                </div>
            )}

            {phase !== 'result' && (
                <div className="mt-8">
                    {/* ONE TAP BEFORE ANY TYPING. The evidence that ordered
                        this (paid cohort, 2026-08-11): 86% arrive on a phone,
                        65% bounce inside a median of SIX seconds, and the
                        single biggest rage-click target on the site was this
                        textarea. The ad promised an instant read; the page
                        opened with homework. A seed tap now RUNS the read -
                        one tap to a real result - and the textarea follows
                        for people ready to make it theirs. */}
                    <p className="text-sm font-medium text-[#2C2C2C] [text-wrap:balance]">
                        Tap a student to see their real read. It takes seconds:
                    </p>
                    <div className="mx-auto mt-3 grid max-w-md grid-cols-2 gap-2">
                        {SEEDS.map((s) => (
                            <button
                                key={s.key}
                                type="button"
                                data-testid={`try-hero-seed-${s.key}`}
                                onClick={() => {
                                    setText(s.text);
                                    setError(null);
                                    track(EVENTS.TRY_HERO_SEED_USED, { seed: s.key, auto_ran: true, ...ep });
                                    run(s.text);
                                }}
                                disabled={phase === 'reading'}
                                // min-h-[44px]: these chips are the main
                                // interaction on this surface and most
                                // first-time visitors are on a phone.
                                className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-[#CFE0D2] bg-[#F4F8F3] px-3 py-2 text-sm font-semibold text-[#1A4D2E] transition-colors hover:bg-[#E8F0E9] disabled:opacity-60"
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>

                    {/* The essay is now the SECOND door, not the wall. */}
                    <p className="mt-5 text-xs text-[#6B6B6B]">
                        Or describe yourself like you'd text a friend. That makes the read yours:
                    </p>
                    <textarea
                        data-testid="try-hero-text"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={PLACEHOLDER}
                        rows={4}
                        maxLength={2000}
                        className="mt-2 w-full rounded-2xl border border-[#E0DED8] bg-white p-5 text-[15px] leading-relaxed shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30"
                    />
                    {error && <p className="mt-2 text-sm text-[#C05838]">{error}</p>}
                    {phase === 'reading' && (
                        // The read genuinely takes 9-13s (measured 2026-08-11),
                        // and 3 of 16 submitters left during a bare spinner.
                        // Narrate the wait honestly - what is happening and
                        // roughly how long - so waiting feels like working.
                        <div data-testid="try-hero-reading" className="mt-4 rounded-xl border border-[#C7DDD0] bg-[#F2F8F4] px-4 py-3 text-sm text-[#1A4D2E]">
                            <ReadingTicker />
                        </div>
                    )}
                    <button
                        type="button"
                        data-testid="try-hero-go"
                        onClick={run}
                        disabled={text.trim().length < 40 || phase === 'reading'}
                        className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#1A4D2E] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#2D6B45] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {phase === 'reading'
                            ? (<><ArrowPathIcon className="h-4 w-4 animate-spin" /> Reading your notes...</>)
                            : (<>Build my picture <ArrowRightIcon className="h-4 w-4" aria-hidden="true" /></>)}
                    </button>
                    {/* #2621: literal. The free read persists nothing server-side; the
                        draft rides this browser's storage into sign-up. */}
                    <p className="mt-2 text-xs text-[#6B6B6B]">{"What you type isn't kept on our side until you sign in."}</p>
                </div>
            )}

            {phase === 'result' && (
                <div className="mt-8 text-left" data-testid="try-hero-result">
                    <div className="rounded-2xl border border-[#E0DED8] bg-white p-4 sm:p-6 shadow-sm">
                        <div className="mb-5">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6B6B6B]">Your notes</h3>
                                {text.trim() !== submittedText.trim() && text.trim().length >= 40 && (
                                    <button
                                        type="button"
                                        data-testid="try-hero-update"
                                        onClick={updateNotes}
                                        disabled={busy}
                                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#1A4D2E] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2D6B45] disabled:opacity-40"
                                    >
                                        {busy ? (<><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Re-reading...</>) : 'Update my picture'}
                                    </button>
                                )}
                            </div>
                            <textarea
                                data-testid="try-hero-notes"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                rows={3}
                                maxLength={2000}
                                className="mt-2 w-full rounded-xl border border-[#E0DED8] bg-[#FDFCF7] p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30"
                            />
                            {error && (
                                <div data-testid="try-hero-error" className="mt-2 rounded-lg border border-[#F0D5C8] bg-[#FCEEE8] px-4 py-3 text-sm font-semibold text-[#C05838]">
                                    {error} - your picture below is unchanged.
                                </div>
                            )}
                        </div>
                        <h3 className="font-serif text-lg font-semibold text-[#2C2C2C]">
                            Here's what we understood
                        </h3>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {chips.map((c) => (
                                <span key={c.label} className="rounded-full border border-[#CFE0D2] bg-[#F4F8F3] px-3 py-1 text-sm text-[#1A4D2E]">
                                    <span className="font-semibold">{c.label}:</span> {c.value}
                                </span>
                            ))}
                            {activities.map((a) => (
                                <span key={`ec-${a.name}`} className="rounded-full border border-[#E0DED8] bg-[#FAFAF8] px-3 py-1 text-sm text-[#4A4A4A]">
                                    {a.name}{a.role ? ` - ${a.role}` : ''}
                                </span>
                            ))}
                            {awards.map((aw) => (
                                <span key={`aw-${aw.name}`} className="rounded-full border border-[#F0D5C8] bg-[#FCF7F4] px-3 py-1 text-sm text-[#C05838]">
                                    <span className="font-semibold">Award:</span> {aw.name}{aw.grade ? ` (grade ${aw.grade})` : ''}
                                </span>
                            ))}
                            {jobs.map((w) => {
                                const role = w.role && !/^job$/i.test(w.role.trim()) ? w.role : '';
                                const label = [role, w.employer].filter(Boolean).join(' - ') || 'Job';
                                return (
                                    <span key={`wk-${w.employer}-${w.role}`} className="rounded-full border border-[#E0DED8] bg-[#FAFAF8] px-3 py-1 text-sm text-[#4A4A4A]">
                                        <span className="font-semibold">Work:</span> {label}
                                    </span>
                                );
                            })}
                            {firstGen && (
                                <span className="rounded-full border border-[#CFE0D2] bg-[#F4F8F3] px-3 py-1 text-sm text-[#1A4D2E]">
                                    First-generation student
                                </span>
                            )}
                        </div>

                        {gated && (
                            <div className="mt-6 rounded-xl border border-[#F0D5C8] bg-[#FCF7F4] p-5" data-testid="try-hero-gpa-ask">
                                <p className="font-semibold text-[#2C2C2C]">
                                    One thing before I read your record - what's your GPA?
                                </p>
                                <p className="mt-1 text-sm text-[#6B6B6B]">
                                    Unweighted (out of 4.0) is best; weighted works too.
                                </p>
                                <div className="mt-3 flex gap-2">
                                    <input
                                        data-testid="try-hero-gpa-input"
                                        type="number" step="0.01" min="0" max="5"
                                        value={gpaDraft}
                                        onChange={(e) => setGpaDraft(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && submitGpa()}
                                        placeholder="3.8"
                                        className={`${inputCls} w-28`}
                                    />
                                    <button
                                        type="button"
                                        data-testid="try-hero-gpa-go"
                                        onClick={submitGpa}
                                        disabled={busy}
                                        className="rounded-lg bg-[#1A4D2E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2D6B45] disabled:opacity-40"
                                    >
                                        {busy ? 'Reading...' : 'Got it'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {!gated && (
                            <div className="mt-6">
                                <h4 className="text-sm font-semibold uppercase tracking-wide text-[#6B6B6B]">
                                    Where do you stand at...
                                </h4>
                                <div className="relative mt-2">
                                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B6B6B]" />
                                    <input
                                        data-testid="try-hero-school-input"
                                        type="text"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && matches[0] && checkSchool(matches[0])}
                                        placeholder="Type a school - UCLA, Michigan, MIT..."
                                        className={`${inputCls} w-full py-3 pl-9`}
                                    />
                                    {matches.length > 0 && (
                                        <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-[#E0DED8] bg-white shadow-lg" data-testid="try-hero-school-options">
                                            {matches.map((m) => (
                                                <button
                                                    key={m.id}
                                                    type="button"
                                                    onClick={() => checkSchool(m)}
                                                    className="block w-full px-4 py-2.5 text-left text-sm hover:bg-[#F4F8F3]"
                                                >
                                                    {m.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {reads.length > 0 && (
                                    <div className="mt-4 space-y-4">
                                        {reads.map((r) => (
                                            <div key={r.university_id} data-testid="try-hero-read" className="rounded-xl border border-[#E0DED8] bg-[#FDFCF7] p-5">
                                                <div className="flex flex-wrap items-center gap-3">
                                                    <span className="font-serif text-lg font-bold text-[#2C2C2C]">{r.name}</span>
                                                    <span className={`rounded-full border px-3 py-0.5 text-sm font-semibold ${BAND_CHIP[r.band] || BAND_CHIP_UNKNOWN}`}>
                                                        {r.band} for you{r.match ? ` · ${r.match}%` : ''}
                                                    </span>
                                                    {r.acceptance_rate != null && (
                                                        <span className="text-xs text-[#6B6B6B]">{rateLine(r.acceptance_rate, r.acceptance_basis)}</span>
                                                    )}
                                                </div>
                                                <ul className="mt-3 space-y-1.5">
                                                    {(r.drivers || []).map((d, i) => (
                                                        <ReasonLine key={d} tone={driverTones(r)[i]}>{d}</ReasonLine>
                                                    ))}
                                                    {r.major_note && (
                                                        <ReasonLine tone={majorNoteTone(r)} testid="try-hero-major-note">{r.major_note}</ReasonLine>
                                                    )}
                                                </ul>
                                            </div>
                                        ))}
                                        {/* One deep-read teaser below ALL chanced colleges - not repeated per card. */}
                                        <div className="mt-2" data-testid="try-hero-locked">
                                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B6B6B]">
                                                Unlock the deep read on every school above
                                            </p>
                                            <div className="grid gap-2 sm:grid-cols-3">
                                                {LOCKED_CARDS.map((c) => (
                                                    <button key={c.title} type="button" onClick={keepIt}
                                                        className="rounded-lg border border-dashed border-[#C9C5BC] bg-white/60 p-3 text-left transition-colors hover:border-[#1A4D2E]">
                                                        <div className="flex items-center gap-1.5 text-xs font-semibold text-[#2C2C2C]">
                                                            <LockClosedIcon className="h-3.5 w-3.5 text-[#C05838]" />{c.title}
                                                        </div>
                                                        <div className="mt-1 text-xs text-[#6B6B6B]">{c.text}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <p className="text-xs text-[#6B6B6B]">{NOTE_TEXT}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="mt-6 rounded-2xl bg-[#1A4D2E] p-5 sm:p-6 text-center">
                        <p className="text-white">
                            <CheckIcon className="mr-1 inline h-4 w-4" />
                            This profile isn't saved yet.
                        </p>
                        <button
                            type="button"
                            data-testid="try-hero-keep"
                            onClick={keepIt}
                            className="mt-3 rounded-lg bg-[#C05838] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#D96A42]"
                        >
                            Sign in to keep it - and run the deep read →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

const NOTE_TEXT = 'Instant surface reads - the deep read (outcomes, gap plan, essay angles) runs when a school joins your list.';
